import React from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface AlertSummaryProps {
  critical: number;
  warning: number;
  caution: number;
  info: number;
}

const COLORS = {
  critical: '#ef4444',
  warning: '#f97316',
  caution: '#eab308',
  info: '#3b82f6',
};

export function AlertSummary({ critical, warning, caution, info }: AlertSummaryProps) {
  const data = [
    { name: 'Critical', value: critical, color: COLORS.critical },
    { name: 'Warning', value: warning, color: COLORS.warning },
    { name: 'Caution', value: caution, color: COLORS.caution },
    { name: 'Info', value: info, color: COLORS.info },
  ].filter(d => d.value > 0);

  const total = critical + warning + caution + info;

  if (total === 0) {
    return (
      <div className="card p-6 text-center">
        <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
          <span className="text-3xl">✓</span>
        </div>
        <p className="text-lg font-medium text-green-400">No Active Alerts</p>
        <p className="text-sm text-slate-400 mt-1">All systems operating normally</p>
      </div>
    );
  }

  return (
    <div className="card p-4">
      <h3 className="text-sm font-medium text-slate-400 mb-4">Alert Distribution</h3>
      <div className="h-48">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              cx="50%"
              cy="50%"
              innerRadius={40}
              outerRadius={70}
              paddingAngle={2}
              dataKey="value"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: '#1e293b',
                border: '1px solid #334155',
                borderRadius: '8px',
              }}
              itemStyle={{
                color: '#f1f5f9',
              }}
              labelStyle={{
                color: '#f1f5f9',
                fontWeight: 'bold',
              }}
            />
            <Legend
              formatter={(value) => <span className="text-slate-300 text-sm">{value}</span>}
            />
          </PieChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-2 mt-4">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-red-500" />
          <span className="text-sm text-slate-300">Critical: {critical}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-orange-500" />
          <span className="text-sm text-slate-300">Warning: {warning}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-yellow-500" />
          <span className="text-sm text-slate-300">Caution: {caution}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-blue-500" />
          <span className="text-sm text-slate-300">Info: {info}</span>
        </div>
      </div>
    </div>
  );
}
